/**
 * Nearest point calculation for ellipse entities.
 */

import type { DrawingModule } from "../../types/DrawingWeb";
import type { DrawingModuleType } from "../../DrawingSingleton";
import { deleteObj } from "../../core/delete";

/**
 * Get the closest point on an ellipse to a given point.
 * Uses Newton-Raphson iteration in the ellipse's local coordinate system.
 *
 * @param module The ODA drawing module
 * @param ellipse The ellipse entity
 * @param givenPoint The point to find the closest point to
 * @returns The closest point on the ellipse, or null if not found
 */
export function getClosestPointToEllipse(
  module: DrawingModuleType,
  ellipse: DrawingModule.OdDbEllipse,
  givenPoint: DrawingModule.OdGePoint3d,
): DrawingModule.OdGePoint3d | null {
  const center = ellipse.center();
  const majorAxis = ellipse.majorAxis();
  const minorAxis = ellipse.minorAxis();

  const a = majorAxis.length(); // semi-major
  const b = minorAxis.length(); // semi-minor

  if (a < 1e-10 || b < 1e-10) {
    deleteObj(minorAxis);
    deleteObj(majorAxis);
    deleteObj(center);
    return null;
  }

  // Compute normalized axis directions (without modifying original vectors)
  const majorNormX = majorAxis.x / a;
  const majorNormY = majorAxis.y / a;
  const majorNormZ = majorAxis.z / a;
  const minorNormX = minorAxis.x / b;
  const minorNormY = minorAxis.y / b;
  const minorNormZ = minorAxis.z / b;

  // Transform pick point to local coordinates
  // Local coords = rotation * (world - center)
  const dx = givenPoint.x - center.x;
  const dy = givenPoint.y - center.y;
  const dz = givenPoint.z - center.z;

  // Project onto local axes (dot products)
  const localPickX = dx * majorNormX + dy * majorNormY + dz * majorNormZ;
  const localPickY = dx * minorNormX + dy * minorNormY + dz * minorNormZ;

  // Newton-Raphson iteration to find closest point parameter
  let t = Math.atan2(localPickY * a, localPickX * b); // initial guess

  for (let i = 0; i < 10; i++) {
    const cosT = Math.cos(t);
    const sinT = Math.sin(t);
    const x = a * cosT;
    const y = b * sinT;
    const ddx = -a * sinT;
    const ddy = b * cosT;

    const fx = (x - localPickX) * ddx + (y - localPickY) * ddy;
    const dfx =
      ddx * ddx +
      ddy * ddy +
      (x - localPickX) * (-a * cosT) +
      (y - localPickY) * (-b * sinT);

    if (Math.abs(dfx) < 1e-10) break;
    t = t - fx / dfx;
  }

  // Local result on ellipse
  const localResultX = a * Math.cos(t);
  const localResultY = b * Math.sin(t);

  // Transform back to world coordinates
  // World = center + localX * majorAxis_normalized + localY * minorAxis_normalized
  const worldResult = new module.OdGePoint3d(
    center.x + localResultX * majorNormX + localResultY * minorNormX,
    center.y + localResultX * majorNormY + localResultY * minorNormY,
    center.z + localResultX * majorNormZ + localResultY * minorNormZ,
  );

  // Cleanup
  deleteObj(minorAxis);
  deleteObj(majorAxis);
  deleteObj(center);

  return worldResult;
}
